import {
  ColumnTypes,
  editableComponents,
  IEditableCellProps,
} from "@/containers/ProjectTemplate";
import { nameof } from "@/helpers";
import {
  IFormFieldOptions,
  IMilestoneTemplate,
  IProjectLeaderApprover,
} from "@/interfaces";
import { useGetFormLookupsQuery } from "@/services";
import { FieldType } from "@/types";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faPlusCircle, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Table } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";

interface IProps {
  milestoneTemplate: IMilestoneTemplate;
  updateMilestone(data: Partial<IMilestoneTemplate>): void;
}

export const TemplateProjectLeaders = ({
  milestoneTemplate,
  updateMilestone,
}: IProps) => {
  const intl = useIntl();

  const { data: { intake } = {} } = useGetFormLookupsQuery();

  const [approvers, setApprovers] = useState<IProjectLeaderApprover[]>([]);

  useEffect(() => {
    if (!milestoneTemplate) return;

    setApprovers(milestoneTemplate.projectLeaderApprovers);
  }, [milestoneTemplate]);

  const updateModalState = (
    updatedApprovers: IProjectLeaderApprover[]
  ): void => {
    setApprovers(updatedApprovers);
    updateMilestone({ projectLeaderApprovers: updatedApprovers });
  };

  const handleAdd = (): void => {
    const newApprover = [
      {
        id: v4(),
      } as IProjectLeaderApprover,
    ];

    const updatedApprovers = newApprover.concat(approvers);

    updateModalState(updatedApprovers);
  };

  const handleDelete = (id: string): void => {
    const updatedApprovers = approvers.filter((approver) => approver.id !== id);
    updateModalState(updatedApprovers);
  };

  const handleUpdate = (row: IProjectLeaderApprover): void => {
    const updatedApprovers = [...approvers];
    const index = updatedApprovers.findIndex(
      (approver) => row.id === approver.id
    );
    const item = updatedApprovers[index];

    updatedApprovers.splice(index, 1, {
      ...item,
      ...row,
    });

    updateModalState(updatedApprovers);
  };

  const defaultColumns: (ColumnTypes[number] & {
    type: FieldType | undefined;
    dataIndex: string;
    required?: boolean;
    editable?: boolean;
    options?: IFormFieldOptions;
    showSearch?: boolean;
    placeholder?: string;
  })[] = [
    {
      title: intl.formatMessage({ id: "milestoneTemplates.columns.region" }),
      dataIndex: nameof<IProjectLeaderApprover>("regionId"),
      key: "region",
      width: "350px",
      type: "DropDown",
      required: true,
      editable: true,
      options: intake?.region ?? {},
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.productCategory",
      }),
      dataIndex: nameof<IProjectLeaderApprover>("productCategories"),
      key: "productCategory",
      width: "750px",
      type: "MultiSelectDropDown",
      required: true,
      editable: true,
      options: intake?.productCategory ?? {},
      placeholder: intl.formatMessage({ id: "general.all" }),
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.notApplicable",
      }),
      dataIndex: nameof<IProjectLeaderApprover>("canSetNotApplicable"),
      key: "notApplicable",
      width: "85px",
      type: "Checkbox",
      editable: true,
    },
    {
      title: (
        <Button
          data-test-id="milestone-template-approvers-add"
          type="link"
          onClick={handleAdd}
          icon={<FontAwesomeIcon icon={faPlusCircle as IconProp} size="xl" />}
        />
      ),
      dataIndex: nameof<IProjectLeaderApprover>("id"),
      key: "delete",
      type: undefined,
      editable: false,
      render: (id: string) => (
        <Button
          data-test-id="milestone-template-approvers-delete"
          type="link"
          onClick={(): void => handleDelete(id)}
          icon={<FontAwesomeIcon icon={faTrash as IconProp} color="red" />}
        />
      ),
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IProjectLeaderApprover) =>
        ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          type: col.type,
          required: col.required,
          options: col.options,
          showSearch: col.showSearch,
          handleSave: handleUpdate,
          placeholder: col.placeholder,
        } as IEditableCellProps<IProjectLeaderApprover>),
    };
  });

  return (
    <Table
      components={editableComponents}
      style={{ margin: "-12px 0px" }}
      tableLayout="fixed"
      className="w-full"
      size="small"
      columns={columns as ColumnTypes}
      dataSource={approvers}
      rowKey="id"
      pagination={false}
    />
  );
};
